import { useEffect, useState } from "react";
import { useFeatureTheme } from "../context/FeatureThemeContext";
import { LockedFeature } from "../components/common/LockedFeature";
import { supportApi } from "../services/supportApi";
import { useToast } from "../context/ToastContext";
import { Card } from "../components/common/Card";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/common/Button";
import { Badge } from "../components/common/Badge";
import { DataTable } from "../components/common/DataTable";
import { Modal } from "../components/common/Modal";
import { Field } from "../components/common/Field";

/* NAMED FOR WHAT THEY ARE, not for what the column calls them.
   app.SupportTickets.category is CHECK (category IN
   ('Bug','Feature','Question','Complaint')) and those four words go to
   the server unchanged -- but "Feature" on a dropdown does not tell
   anybody that this is where a suggestion goes, which is why it was
   asked whether the app needed a separate suggestion box. It does not:
   it needed this list to say so. */
const CATEGORIES = [
  { value: "Bug", label: "Something is broken" },
  { value: "Feature", label: "A suggestion — something you wish it did" },
  { value: "Question", label: "A question" },
  { value: "Complaint", label: "Something is wrong with how it works" },
];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUS_TONE = { Open: "info", InProgress: "warning", Resolved: "success", Closed: "neutral" };

function SupportPageContent({ enabled }) {
  const { notify } = useToast();
  const [tickets, setTickets] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", category: "Question", priority: "Medium" });
  /* FEEDBACK, WHICH IS NOT A TICKET (#432). app.UserFeedback has been read
     since V001 -- by the admin sentiment surface and by the GDPR export --
     and written by nothing, so both reported on a table nobody could fill.
     Kept separate from the ticket form because they are different acts: a
     ticket is a request for help that somebody owes you an answer to,
     feedback is an opinion that expects no reply. Folding them together
     would either open a ticket nobody closes for every opinion, or leave a
     real problem with nowhere to be answered. */
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState("");
  const [sending, setSending] = useState(false);

  async function load() {
    const r = await supportApi.list();
    setTickets(r.tickets);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (enabled) load(); }, [enabled]);

  async function submit() {
    if (!form.subject.trim()) return notify("Subject is required", "warning");
    try {
      await supportApi.create(form);
      notify("Ticket created", "success");
      setOpen(false);
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not create ticket", "danger");
    }
  }

  async function sendFeedback() {
    // The star is the required half; the words are optional. A rating of
    // 0 is "not chosen", not a score -- the column refuses anything
    // outside 1-5 (CK_UserFeedback_rating).
    if (!rating) return notify("Pick a rating first", "warning");
    setSending(true);
    try {
      await supportApi.sendFeedback({
        rating,
        comment: comment.trim() || undefined,
        feature_context: "Support",
      });
      notify("Thanks — that went through", "success");
      setRating(0);
      setComment("");
    } catch (err) {
      notify(err.apiMessage || "Could not send that feedback", "danger");
    } finally {
      setSending(false);
    }
  }

  return (
    <div>
      <PageHeader title="Support">
        {/* "New Ticket" told people with an idea that this was not for them
            -- nobody with a suggestion thinks "I should raise a support
            ticket". The capability was always here; the label hid it. */}
        <Button onClick={() => setOpen(true)}>Report or suggest</Button>
      </PageHeader>
      <Card>
        <DataTable
          rows={tickets}
          loading={!tickets}
          rowKey="ticket_id"
          emptyTitle="No support tickets yet"
          emptyDescription="Raise a ticket if something goes wrong or you need a hand, and follow the reply here."
          columns={[
            { key: "subject", header: "Subject" },
            { key: "category", header: "Category" },
            { key: "priority", header: "Priority" },
            { key: "status", header: "Status", render: (r) => <Badge tone={STATUS_TONE[r.status]}>{r.status}</Badge> },
            { key: "created_date", header: "Created", render: (r) => new Date(r.created_date).toLocaleDateString() },
          ]}
        />
      </Card>

      <Card title="How are we doing?" className="section-spacing">
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          Not a support request — just what you think. Nobody will reply, and
          it helps decide what gets built next.
        </p>
        <div role="radiogroup" aria-label="Rating out of 5"
             style={{ display: "flex", gap: "var(--space-2)", marginBottom: "var(--space-3)" }}>
          {[1, 2, 3, 4, 5].map((n) => (
            /* REAL BUTTONS WITH REAL LABELS. A row of stars is the obvious
               design and is unusable without sight -- and this app has been
               told three times that a control styled as decoration reads as
               decoration. Each one says what it means. */
            <Button
              key={n}
              type="button"
              size="sm"
              variant={rating === n ? "primary" : "secondary"}
              aria-pressed={rating === n}
              aria-label={`${n} out of 5`}
              onClick={() => setRating(n)}
            >
              {n}
            </Button>
          ))}
        </div>
        <textarea id="support-page-comment" name="comment"
          className="touch-target"
          aria-label="Anything you want to add"
          placeholder="Anything you want to add (optional)"
          value={comment}
          // 2000, matching app.UserFeedback.comment. A box that accepts
          // more than the column does refuses the text only after Send.
          maxLength={2000}
          rows={3}
          onChange={(e) => setComment(e.target.value)}
          style={{ width: "100%", marginBottom: "var(--space-3)" }}
        />
        <Button onClick={sendFeedback} disabled={sending || !rating}>Send feedback</Button>
      </Card>

      {/* LABELS, NOT PLACEHOLDERS. All three controls were bare inputs
          whose only description was placeholder text -- which disappears
          the moment somebody types, is not reliably announced, and left
          two dropdowns with no accessible name at all. Field gives each
          one a real <label>, and the hints are where this form finally
          says out loud that a suggestion belongs here. */}
      <Modal open={open} onClose={() => setOpen(false)}
             title="Report a problem, or suggest something"
             footer={<Button onClick={submit}>Send</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="What is it about?" hint="Suggestions are as welcome as problems.">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={form.category}
                      onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}>
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            )}
          </Field>
          <Field label="In a line" hint="A short summary — the detail can follow in a reply.">
            {(id, aria) => (
              <input id={id} {...aria} className="touch-target" maxLength={200}
                     value={form.subject}
                     onChange={(e) => setForm((f) => ({ ...f, subject: e.target.value }))} />
            )}
          </Field>
          <Field label="How urgent?" hint="Be honest — everything marked Critical means nothing is.">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={form.priority}
                      onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
              </select>
            )}
          </Field>
        </div>
      </Modal>
    </div>
  );
}

/* A locked account still sees the page, blurred behind the upgrade prompt,
   and does not ask the server for data it will refuse (it used to, and the
   console filled with 403s). */
export function SupportPage() {
  const { hasFeature } = useFeatureTheme();
  const enabled = hasFeature("SupportFeedback");
  return (
    <LockedFeature enabled={enabled} featureLabel="Support" previewText="Report a problem or suggest an improvement, and follow the replies.">
      <SupportPageContent enabled={enabled} />
    </LockedFeature>
  );
}
